/**
 * Two or three equal halves, `control-height-lg`, `radius-md`, 1px
 * `border-default`.
 *
 * Two surfaces use it and they are styled differently on purpose:
 *
 * - **The asset selector** (components.md §1) is neutral: BTC / ETH, selected
 *   half `bg-active` + `text-primary` + a 2px `accent-solid` bottom border. It
 *   is a segmented control and not a dropdown because there are exactly two
 *   series and a dropdown to choose between two things is a wasted click. It
 *   used the `sub` slot for each asset's live index price and no longer does:
 *   two stacked numbers inside one `control-height` left neither line room,
 *   and the price is on the chart axis and in `<MarketStrip>` already.
 * - **`<OutcomeToggle>`** (§2.3, Phase 6) is the same geometry in `up-solid` /
 *   `down-solid`, and doubles as the odds display via the same `sub` slot.
 *
 * The `up`/`down` tones exist here so both share one keyboard model and one set
 * of focus behaviours; the meaning stays with the caller.
 *
 * **Phase 10 gave it the keyboard model it had been claiming.** The role has
 * said `radiogroup` since Phase 2 and every segment was an ordinary tabbable
 * `<button>` with no arrow handling — so the control announced itself as a radio
 * group and behaved like a toolbar, and a keyboard user crossing the order
 * ticket had to Tab past every option of every group. `radio-group.ts` holds the
 * model; this and `<OutcomeToggle>` are its two callers.
 */

import { useRef } from 'react'
import type { ReactNode } from 'react'
import { cx } from '@/lib/cx'
import { nextRadioIndex, radioTabIndex } from './radio-group'

export type SegmentTone = 'neutral' | 'up' | 'down'

export interface Segment<T extends string> {
  id: T
  label: ReactNode
  /** Second line — the live index price, or the outcome's price in cents. */
  sub?: ReactNode
  tone?: SegmentTone
  disabled?: boolean
}

const SELECTED: Record<SegmentTone, string> = {
  neutral: 'bg-bg-active text-text-primary shadow-[inset_0_-2px_0_0_var(--ud-color-accent-solid)]',
  up: 'bg-up-solid text-up-on-solid',
  down: 'bg-down-solid text-down-on-solid',
}

const UNSELECTED: Record<SegmentTone, string> = {
  neutral: 'bg-bg-raised text-text-secondary hover:bg-bg-hover hover:text-text-primary',
  up: 'bg-bg-raised text-text-secondary hover:bg-up-bg hover:text-up-text',
  down: 'bg-bg-raised text-text-secondary hover:bg-down-bg hover:text-down-text',
}

export interface SegmentedControlProps<T extends string> {
  segments: readonly Segment<T>[]
  value: T
  onChange: (id: T) => void
  label: string
  size?: 'base' | 'lg'
  className?: string
}

export function SegmentedControl<T extends string>({
  segments,
  value,
  onChange,
  label,
  size = 'lg',
  className,
}: SegmentedControlProps<T>) {
  // The group is one tab stop and the arrows move within it, so focus has to be
  // moved imperatively — React cannot express "and now that element is focused".
  const groupRef = useRef<HTMLDivElement>(null)
  const selectedIndex = segments.findIndex((s) => s.id === value)

  return (
    <div
      ref={groupRef}
      role="radiogroup"
      onKeyDown={(event) => {
        const to = nextRadioIndex(event.key, selectedIndex, segments.length, (i) => !segments[i]?.disabled)
        if (to === null) return
        // ArrowUp/ArrowDown would otherwise scroll the panel the ticket sits in.
        event.preventDefault()
        const target = segments[to]
        if (!target) return
        onChange(target.id)
        groupRef.current?.querySelectorAll<HTMLButtonElement>('[role=radio]')[to]?.focus()
      }}
      aria-label={label}
      className={cx(
        'inline-grid overflow-hidden rounded-md border border-border-default',
        // **Below 768px the segments carry the height, not the group.** A fixed
        // 44px group has 42px segments, because the 1px border is inside it —
        // tall enough to look right and 2px under the floor (Phase 10).
        size === 'lg' ? 'md:h-[var(--ud-control-height-lg)]' : 'md:h-[var(--ud-control-height)]',
        className,
      )}
      style={{ gridTemplateColumns: `repeat(${segments.length}, minmax(0, 1fr))` }}
    >
      {segments.map((seg, index) => {
        const selected = seg.id === value
        const tone = seg.tone ?? 'neutral'
        return (
          <button
            key={seg.id}
            type="button"
            role="radio"
            aria-checked={selected}
            tabIndex={radioTabIndex(index, selectedIndex)}
            disabled={seg.disabled}
            onClick={() => onChange(seg.id)}
            className={cx(
              'flex flex-col items-center justify-center gap-0 px-2 leading-none',
            // Both axes below 768px: a segment's width is its content's, so
            // `BTC —` before the first index frame is 42px and a miss, while
            // `BTC $63,180` a second later is not. A hit target that depends on
            // whether data has arrived is not a hit target.
            'min-h-[var(--ud-tap-min)] min-w-[var(--ud-tap-min)] md:min-h-0 md:min-w-0',
              'transition-colors duration-[var(--ud-duration-fast)]',
              'focus-visible:shadow-focus focus-visible:outline-none',
              'text-body font-semibold',
              seg.disabled
                ? 'cursor-not-allowed bg-bg-active text-text-disabled'
                : selected
                  ? SELECTED[tone]
                  : UNSELECTED[tone],
            )}
          >
            <span className="inline-flex items-center gap-1">{seg.label}</span>
            {seg.sub ? (
              <span className="ud-num text-caption font-normal opacity-80">{seg.sub}</span>
            ) : null}
          </button>
        )
      })}
    </div>
  )
}
